/**
 * ОБЩИЙ редактор промпта с пилюлями — сайт, мастерская и конструктор карточек в админке
 * (мастер — shared/prompt-vars, копии кладёт scripts/sync_prompt_vars.sh; править только
 * мастер). Пилюли, их подписи, цвета и токены провайдера — из одного определения
 * переменных (spec.json), поэтому три места не расходятся.
 *
 * Два режима одного и того же (владелец 25.09, «универсальный инструмент»):
 *   «phrase»   — сайт и мастерская: значение — ТОТ ЖЕ текст промпта с каноническими
 *                фразами («Image 1», «the reference video»), пилюли — их отображение;
 *                подпись пилюли = токен, который получит выбранная модель (@Image1).
 *   «template» — админка: значение — шаблон карточки с токенами ({photo1}, {video},
 *                {variant}); пилюля показывает «Фото 1» и токен модели карточки.
 * В обоих режимах в данные уходит serialize() — чистая строка без разметки; мини-фото
 * в пилюлях только для человека. Механика чипов: contentEditable=false-спаны,
 * serialize обходом DOM, emitted-ref против лишних перестроек (владелец 2026-08-23).
 */
import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react'
import { createPortal } from 'react-dom'
import {
	assetIdOf,
	assetIdsInPrompt,
	mapCaret,
	splitPromptByPhrases,
	splitPromptByTokens,
	tokenForPhrase,
	tokenFromTemplate,
	phraseForRank,
	phraseForToken,
	refTokenLabel,
	FIRST_FRAME_PHRASES,
	LAST_FRAME_PHRASE,
	CHARACTER_PHRASE,
	VIDEO_REF_PHRASE,
	AUDIO_REF_PHRASE,
	KEY_BY_KIND,
	normalizeRefs,
	type PillToken,
	type PromptKind,
	type PromptPart,
	type RefConversion,
} from './phrases'
import { adminLabel, promptVar, varLabel, type PromptRefStyle, pillRankCls } from './index'

export type PillEditorMode = 'phrase' | 'template'

export interface PillPhotoSlot {
	/** Ранг = позиция в референсах (1..N) — совпадает с «the N-th image». */
	rank: number
	/** Подпись слота карточки («Ваше фото»); нет — просто «Фото N». */
	label?: string
	/** Превью загруженного фото; null — слот пуст. */
	url?: string | null
}

export interface PillSlots {
	kind: PromptKind
	photos: PillPhotoSlot[]
	/** label — подпись под именем (карточка товара: «основа»); нет — «ваше фото». */
	firstFrame?: { url?: string | null; label?: string }
	lastFrame?: { url?: string | null; label?: string }
	character?: { url?: string | null; name?: string }
	videoRef?: { url?: string | null }
	audioRef?: { url?: string | null; name?: string }
	/** Применимость входов у ТЕКУЩЕЙ модели: false — пилюля прячется целиком
	 *  (текст в состоянии остаётся, при возврате к модели пилюля вернётся). */
	caps?: Partial<Record<PillToken['kind'], boolean>>
	/** Синтаксис ссылок выбранной модели: подпись пилюли = токен, который получит модель
	 *  (@Image1 у Seedance, @image_1 у прямого Kling). Текст промпта не меняется —
	 *  смена модели только перерисовывает подписи. */
	refStyle?: PromptRefStyle | null
	/** Режим «шаблон»: подпись пилюли {value} — label «Поля юзера» карточки. */
	valueLabel?: string
	/** Ассеты сцены по id — для чипов упоминаний @{uuid}@: имя, миниатюра (превью), тип. */
	assets?: Record<string, PillAsset>
}

export interface PillAsset {
	name: string
	url?: string | null
	kind: 'character' | 'location' | 'prop' | string
}

/** «@» у каретки: что набрано после него и где рисовать меню (координаты вьюпорта). */
export interface MentionQuery {
	query: string
	left: number
	top: number
	bottom: number
}

const ASSET_KIND_LABEL: Record<string, string> = { character: 'аватар', location: 'локация', prop: 'предмет' }

export interface PromptPillEditorHandle {
	focus: () => void
	/** Вставить текст в позицию курсора (или в конец, если фокуса не было). */
	insertText: (text: string) => void
	/** Подвести символ pos к видимой части поля (аналог scrollCaretIntoView). */
	scrollPosIntoView: (pos: number) => void
	/** Заменить набранное «@запрос» у каретки токеном упоминания (выбор в меню «@»). */
	insertMention: (token: string) => void
	el: HTMLDivElement | null
}

interface PopState {
	token: PillToken
	/** Фиксированные координаты вьюпорта (поповер в portal — виджет его не режет). */
	left: number
	top: number
	below: boolean
}

const KIND_CLS: Record<string, string> = {
	first_frame: 'gm__ppill--frame1', last_frame: 'gm__ppill--frame2', character: 'gm__ppill--char',
	video_ref: 'gm__ppill--video', audio_ref: 'gm__ppill--audio', variant: 'gm__ppill--variant', value: 'gm__ppill--value',
}

function assetView(token: PillToken, slots: PillSlots): { cls: string; main: string; sub?: string; empty: boolean } {
	const a = token.assetId ? slots.assets?.[token.assetId] : undefined
	// Ассет ещё не подгружен (восстановленный промпт) — чип-заглушка, имя подтянет хост.
	return { cls: `gm__ppill--asset gm__ppill--asset-${a?.kind || 'unknown'}`, main: a?.name || '…', empty: !a }
}

function pillView(token: PillToken, slots: PillSlots, mode: PillEditorMode): { cls: string; main: string; sub?: string; empty: boolean; off?: boolean } {
	if (token.kind === 'asset') return assetView(token, slots)
	if (mode === 'template') return templateView(token, slots)
	// Вход не применим к текущей модели — пилюля скрыта (владелец 24.08:
	// «аудио не очистилось» — янтарные чужие пилюли выглядят поломкой).
	if (slots.caps && slots.caps[token.kind] === false) {
		const v = pillViewInner(token, slots)
		return { ...v, off: true }
	}
	return pillViewInner(token, slots)
}

/** Режим «шаблон» (конструктор карточек): «Фото 1 · @Image1 · подпись слота», без файлов. */
function templateView(token: PillToken, slots: PillSlots): { cls: string; main: string; sub?: string; empty: boolean } {
	if (token.kind === 'asset') return assetView(token, slots)
	const key = KEY_BY_KIND[token.kind]
	const def = promptVar(key)
	const n = token.rank ?? 1
	const cls = token.kind === 'photo' ? pillRankCls(n) : KIND_CLS[token.kind] || 'gm__ppill--variant'
	let sub: string | undefined
	if (token.kind === 'photo') sub = slots.photos[n - 1]?.label || undefined
	else if (token.kind === 'value') sub = slots.valueLabel || undefined
	else sub = def.admin_sub || undefined
	const modelToken = refTokenLabel(token, slots.refStyle)
	if (modelToken) sub = [modelToken, sub].filter(Boolean).join(' · ')
	return { cls, main: adminLabel(key, n), sub, empty: false }
}

/** Файл входа пилюли — для мини-фото в самой пилюле и превью в поповере. */
function tokenUrl(token: PillToken, slots: PillSlots): string | null | undefined {
	switch (token.kind) {
		case 'photo':
			return slots.photos[(token.rank ?? 1) - 1]?.url
		case 'first_frame':
			return slots.firstFrame?.url
		case 'last_frame':
			return slots.lastFrame?.url
		case 'video_ref':
			return slots.videoRef?.url
		case 'audio_ref':
			return slots.audioRef?.url
		case 'asset':
			return token.assetId ? slots.assets?.[token.assetId]?.url : null
		default:
			return slots.character?.url
	}
}

function pillViewInner(token: PillToken, slots: PillSlots): { cls: string; main: string; sub?: string; empty: boolean } {
	if (token.kind === 'photo') {
		const slot = slots.photos[(token.rank ?? 1) - 1]
		const empty = !slot?.url
		return {
			cls: `${pillRankCls(token.rank ?? 1)}${empty ? ' gm__ppill--empty' : ''}`,
			main: refTokenLabel(token, slots.refStyle) ?? varLabel('photo', slots.kind, token.rank ?? 1),
			sub: empty ? promptVar('photo').empty : slot?.label || undefined,
			empty,
		}
	}
	if (token.kind === 'first_frame') {
		const empty = !slots.firstFrame?.url
		return { cls: `gm__ppill--frame1${empty ? ' gm__ppill--empty' : ''}`, main: varLabel('first_frame', slots.kind), sub: empty ? promptVar('first_frame').empty : slots.firstFrame?.label || promptVar('first_frame').sub, empty }
	}
	if (token.kind === 'last_frame') {
		const empty = !slots.lastFrame?.url
		return { cls: `gm__ppill--frame2${empty ? ' gm__ppill--empty' : ''}`, main: varLabel('last_frame'), sub: empty ? promptVar('last_frame').empty : slots.lastFrame?.label, empty }
	}
	if (token.kind === 'video_ref') {
		const empty = !slots.videoRef?.url
		return { cls: `gm__ppill--video${empty ? ' gm__ppill--empty' : ''}`, main: refTokenLabel(token, slots.refStyle) ?? varLabel('video'), sub: empty ? promptVar('video').empty : promptVar('video').sub, empty }
	}
	if (token.kind === 'audio_ref') {
		const empty = !slots.audioRef?.url
		return { cls: `gm__ppill--audio${empty ? ' gm__ppill--empty' : ''}`, main: refTokenLabel(token, slots.refStyle) ?? varLabel('audio'), sub: empty ? promptVar('audio').empty : slots.audioRef?.name || promptVar('audio').sub, empty }
	}
	if (token.kind === 'variant' || token.kind === 'value') {
		// Токены шаблона в режиме «фразы» не встречаются — на всякий случай рисуем как есть.
		return { cls: KIND_CLS[token.kind], main: adminLabel(KEY_BY_KIND[token.kind]), empty: false }
	}
	if (token.kind === 'asset') return assetView(token, slots)
	const empty = !slots.character?.url
	return { cls: `gm__ppill--char${empty ? ' gm__ppill--empty' : ''}`, main: varLabel('character'), sub: slots.character?.name || (empty ? promptVar('character').empty : undefined), empty }
}

const PromptPillEditor = forwardRef<PromptPillEditorHandle, {
	value: string
	onChange: (v: string) => void
	placeholder?: string
	disabled?: boolean
	maxLength?: number
	className?: string
	slots: PillSlots
	/** «phrase» — сайт/мастерская (по умолчанию), «template» — конструктор карточек. */
	mode?: PillEditorMode
	/** Ссылки, записанные «руками» (@video1, <<<image_1>>>), только что превращены в переменные/фразы — чтобы
	 *  место могло показать, что именно распознано. */
	onRefsConverted?: (items: RefConversion[]) => void
	/** «@» у каретки: хост рисует меню ассетов (null — меню закрыть). Нет пропа — «@» просто текст. */
	onMention?: (m: MentionQuery | null) => void
	/** Клавиши, пока меню «@» открыто: true — хост обработал (стрелки, Enter, Tab, Esc), редактор не реагирует. */
	onMentionKey?: (key: string) => boolean
}>(function PromptPillEditor({ value, onChange, placeholder, disabled, maxLength, className, slots, mode = 'phrase', onRefsConverted, onMention, onMentionKey }, outerRef) {
	// Разбор текста и распознавание пилюль — по режиму; всё остальное одинаково.
	const splitValue = (v: string): PromptPart[] => (mode === 'template' ? splitPromptByTokens(v) : splitPromptByPhrases(v))
	const tokenOf = (text: string): PillToken | undefined => {
		// Чип ассета (@{uuid}@) — пилюля в обоих режимах: без этого наведение и клик по нему ничего не показывали.
		const assetId = assetIdOf(text)
		if (assetId) return { phrase: text, kind: 'asset', assetId }
		return mode === 'template' ? tokenFromTemplate(text) : tokenForPhrase(text)
	}
	const ref = useRef<HTMLDivElement>(null)
	const wrapRef = useRef<HTMLDivElement>(null)
	const onChangeRef = useRef(onChange)
	useEffect(() => { onChangeRef.current = onChange })
	const onRefsConvertedRef = useRef(onRefsConverted)
	useEffect(() => { onRefsConvertedRef.current = onRefsConverted })
	// Последнее значение, которое редактор видел/эмитил: совпадает — DOM не
	// перестраиваем (свой ввод), отличается — пришло извне (карточка, анимация).
	const emitted = useRef<string | null>(null)
	const slotsRef = useRef(slots)
	useEffect(() => { slotsRef.current = slots })
	const [pop, setPop] = useState<PopState | null>(null)
	const popTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
	const dragChip = useRef<HTMLElement | null>(null)
	// Последняя позиция каретки ВНУТРИ поля (владелец 04.09 «вставлять туда,
	// где курсор»): клик по кнопке-чипу уводит фокус, а вставка должна лечь
	// в текст туда, где юзер оставил каретку.
	const lastCaret = useRef<number | null>(null)

	const serialize = (): string => {
		const root = ref.current
		if (!root) return ''
		let out = ''
		const walk = (node: Node) => {
			node.childNodes.forEach((ch) => {
				if (ch.nodeType === Node.TEXT_NODE) { out += (ch as Text).data; return }
				if (ch.nodeType !== Node.ELEMENT_NODE) return
				const el = ch as HTMLElement
				if (el.dataset && el.dataset.phrase != null) { out += el.dataset.phrase; return }
				if (el.tagName === 'BR') { out += '\n'; return }
				if (/^(DIV|P)$/.test(el.tagName) && out && !out.endsWith('\n')) out += '\n'
				walk(el)
			})
		}
		walk(root)
		return out.replace(/\u00A0/g, ' ')
	}

	/** Текстовая позиция каретки (пилюля = длина её фразы, BR = 1). */
	const caretOffset = (): number | null => {
		const root = ref.current
		const sel = window.getSelection()
		if (!root || !sel || sel.rangeCount === 0) return null
		const r = sel.getRangeAt(0)
		if (!root.contains(r.startContainer)) return null
		let pos = 0
		let done = false
		const walk = (node: Node): boolean => {
			for (const ch of Array.from(node.childNodes)) {
				if (ch === r.startContainer) {
					pos += ch.nodeType === Node.TEXT_NODE ? r.startOffset : 0
					done = true
					return true
				}
				if (ch.nodeType === Node.TEXT_NODE) {
					pos += (ch as Text).data.length
				} else if (ch.nodeType === Node.ELEMENT_NODE) {
					const el = ch as HTMLElement
					if (el.dataset && el.dataset.phrase != null) pos += el.dataset.phrase.length
					else if (el.tagName === 'BR') pos += 1
					else if (walk(el)) return true
					if (el === r.startContainer) { done = true; return true }
				}
			}
			return false
		}
		walk(root)
		return done || r.startContainer === root ? pos : null
	}

	/** Поставить каретку в текстовую позицию (внутрь пилюли — после неё). */
	const setCaretOffset = (pos: number) => {
		const root = ref.current
		if (!root) return
		let rest = pos
		const range = document.createRange()
		let placed = false
		const walk = (node: Node): boolean => {
			for (const ch of Array.from(node.childNodes)) {
				if (ch.nodeType === Node.TEXT_NODE) {
					const len = (ch as Text).data.length
					if (rest <= len) { range.setStart(ch, rest); placed = true; return true }
					rest -= len
				} else if (ch.nodeType === Node.ELEMENT_NODE) {
					const el = ch as HTMLElement
					if (el.dataset && el.dataset.phrase != null) {
						const len = el.dataset.phrase.length
						if (rest <= len) { range.setStartAfter(el); placed = true; return true }
						rest -= len
					} else if (el.tagName === 'BR') {
						if (rest < 1) { range.setStartBefore(el); placed = true; return true }
						rest -= 1
					} else if (walk(el)) return true
				}
			}
			return false
		}
		walk(root)
		if (!placed) { range.selectNodeContents(root); range.collapse(false) }
		else range.collapse(true)
		const sel = window.getSelection()
		sel?.removeAllRanges()
		sel?.addRange(range)
	}

	// «@» у каретки (меню ассетов): начало строки/после пробела или скобки, дальше до 30 знаков имени.
	const onMentionRef = useRef(onMention)
	useEffect(() => { onMentionRef.current = onMention })
	const mentionRef = useRef<{ start: number; query: string } | null>(null)
	const checkMention = () => {
		if (!onMentionRef.current) return
		const root = ref.current
		const sel = window.getSelection()
		const pos = caretOffset()
		let found: { start: number; query: string } | null = null
		if (root && sel && sel.isCollapsed && pos != null) {
			const before = serialize().slice(0, pos)
			const m = /(?:^|[\s(«"'])@([\p{L}\p{N}_\- ]{0,30})$/u.exec(before)
			if (m && !/ {2}$/.test(m[1])) found = { start: pos - m[1].length - 1, query: m[1] }
		}
		mentionRef.current = found
		if (!found || !sel || sel.rangeCount === 0) {
			onMentionRef.current(null)
			return
		}
		const r = sel.getRangeAt(0).cloneRange()
		let rect = r.getBoundingClientRect()
		if (!rect.width && !rect.height && root) rect = root.getBoundingClientRect()
		onMentionRef.current({ query: found.query, left: rect.left, top: rect.top, bottom: rect.bottom })
	}

	useEffect(() => {
		const onSel = () => {
			const p = caretOffset()
			if (p != null) lastCaret.current = p
			if (mentionRef.current) checkMention()
		}
		document.addEventListener('selectionchange', onSel)
		return () => document.removeEventListener('selectionchange', onSel)
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	/** Набранная руками фраза становится пилюлей на лету (замечание владельца
	 *  23.08): конвертируем матчи, которые каретка уже «покинула» — не тот, что
	 *  печатается прямо сейчас (иначе «the first image» нельзя дописать до
	 *  «imagery»). Пилюли в DOM не трогаем — только текстовые куски. */
	const pillifyTyped = (v: string) => {
		const root = ref.current
		if (!root) return
		const parts = splitValue(v)
		const phraseCount = parts.filter((p) => p.type === 'pill').length
		const chipCount = root.querySelectorAll('[data-phrase]').length
		if (phraseCount <= chipCount) return
		const caret = caretOffset()
		// Каретка стоит СРАЗУ за фразой — юзер, возможно, ещё печатает слово
		// («the first image…ry»): ждём разделителя, конвертировать рано.
		let off = 0
		let blocked = false
		for (const part of parts) {
			const end = off + part.value.length
			if (part.type === 'pill' && caret === end) blocked = true
			off = end
		}
		if (blocked) return
		rebuild(v)
		if (caret != null) setCaretOffset(caret)
	}

	/** Ссылки «руками» → каноническая форма режима (normalizeRefs, то же правило, что у сервера). Запись, которую
	 *  юзер ещё печатает (кончается у каретки), ждёт разделителя. true — текст заменён и поле пересобрано. */
	const convertRefs = (v: string, caret: number | null, whole: boolean): boolean => {
		const scan = normalizeRefs(v, mode, slotsRef.current.kind, whole ? undefined : caret ?? undefined)
		if (!scan.converted.length) return false
		emitted.current = scan.text
		onChangeRef.current(scan.text)
		rebuild(scan.text)
		if (caret != null) setCaretOffset(scan.mapPos(caret))
		onRefsConvertedRef.current?.(scan.converted)
		return true
	}

	const emit = () => {
		const v = serialize()
		if (convertRefs(v, caretOffset(), false)) return
		emitted.current = v
		onChangeRef.current(v)
		pillifyTyped(v)
		checkMention()
	}

	const hidePopSoon = () => {
		if (popTimer.current) clearTimeout(popTimer.current)
		popTimer.current = setTimeout(() => setPop(null), 160)
	}
	const showPopFor = (chip: HTMLElement) => {
		if (popTimer.current) clearTimeout(popTimer.current)
		const token = tokenOf(chip.dataset.phrase || '')
		if (!token) return
		const cr = chip.getBoundingClientRect()
		// Поповер над пилюлей (как в макете); под ней — только когда сверху не
		// хватает места в окне. Координаты фиксированные: portal в body, края
		// виджета его не обрезают (замечание владельца 23.08).
		const below = cr.top < 230
		setPop({
			token,
			left: Math.max(8, Math.min(cr.left, window.innerWidth - 212)),
			top: below ? cr.bottom + 6 : cr.top - 6,
			below,
		})
	}

	const buildChip = (token: PillToken, phrase: string): HTMLElement => {
		const view = pillView(token, slotsRef.current, mode)
		const chip = document.createElement('span')
		chip.className = `gm__ppill ${view.cls}${view.off ? ' gm__ppill--off' : ''}`
		chip.contentEditable = 'false'
		chip.dataset.phrase = phrase
		// Мини-фото прямо в пилюле (владелец 04.09, референс — Kling): видно, на
		// какое фото ссылка, без наведения; крупнее — по-прежнему в поповере.
		// Видео/аудио — не картинка, у них миниатюры нет.
		const thumbUrl = mode === 'template' || token.kind === 'video_ref' || token.kind === 'audio_ref' ? null : tokenUrl(token, slotsRef.current)
		if (thumbUrl) {
			const im = document.createElement('img')
			im.className = 'gm__ppill__img'
			im.src = thumbUrl
			im.alt = ''
			im.draggable = false
			// Файла уже нет (ссылка истекла, фото удалили) — пилюля остаётся без миниатюры, а не с битой картинкой.
			im.addEventListener('error', () => im.remove())
			chip.appendChild(im)
		}
		const b = document.createElement('span')
		b.className = 'gm__ppill__main'
		b.textContent = view.main
		chip.appendChild(b)
		if (view.sub) {
			const i = document.createElement('span')
			i.className = 'gm__ppill__sub'
			i.textContent = view.sub
			chip.appendChild(i)
		}
		const x = document.createElement('span')
		x.className = 'gm__ppill__x'
		x.textContent = '×'
		x.setAttribute('title', mode === 'template' ? 'Убрать переменную из промпта' : 'Убрать ссылку из промпта')
		x.addEventListener('mousedown', (e) => { e.preventDefault(); e.stopPropagation() })
		x.addEventListener('click', (e) => {
			e.preventDefault()
			e.stopPropagation()
			chip.remove()
			setPop(null)
			emit()
		})
		chip.appendChild(x)
		// Наведение — десктоп; клик по пилюле — мобильный тап (и десктоп тоже).
		chip.addEventListener('mouseenter', () => showPopFor(chip))
		chip.addEventListener('mouseleave', hidePopSoon)
		chip.addEventListener('click', (e) => { e.preventDefault(); showPopFor(chip) })
		// Перетаскивание пилюли внутри промпта (замечание владельца 23.08) —
		// как в админ-редакторе: узел ПЕРЕМЕЩАЕТСЯ на drop.
		chip.draggable = true
		chip.addEventListener('dragstart', (e) => {
			dragChip.current = chip
			setPop(null)
			e.dataTransfer?.setData('text/plain', chip.dataset.phrase || '')
			if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
		})
		chip.addEventListener('dragend', () => { dragChip.current = null })
		return chip
	}

	/** Полная пересборка DOM из значения (внешнее обновление). */
	const rebuild = (v: string) => {
		const root = ref.current
		if (!root) return
		root.textContent = ''
		for (const part of splitValue(v)) {
			if (part.type === 'pill' && part.token) root.appendChild(buildChip(part.token, part.value))
			else {
				const lines = part.value.split('\n')
				lines.forEach((line, i) => {
					if (i > 0) root.appendChild(document.createElement('br'))
					if (line) root.appendChild(document.createTextNode(line))
				})
			}
		}
	}

	// Значение пришло извне (применение карточки, печатающая анимация, идея-мастер).
	useEffect(() => {
		if (value === emitted.current) return
		// Шаблон карточки (админка): сохранённые ссылки «руками» (@video1) сразу становятся переменными-пилюлями —
		// всё, что модель поймёт как поле, видно пилюлей с первого взгляда (владелец 28.09). Замена уходит в черновик
		// через onChange и остаётся, только если карточку сохранят. В режиме фраз внешний текст не трогаем:
		// печатающая анимация подаёт его кусками.
		if (mode === 'template') {
			const scan = normalizeRefs(value, 'template')
			if (scan.converted.length) {
				emitted.current = scan.text
				rebuild(scan.text)
				onChangeRef.current(scan.text)
				onRefsConvertedRef.current?.(scan.converted)
				return
			}
		}
		// Поле в фокусе (юзер печатает, а текст поменяли снаружи — убрали чип удалённого ассета, подставили
		// переменную): каретку возвращаем на её место в новом тексте. Без этого пересборка бросала её в начало поля.
		const caret = ref.current && document.activeElement === ref.current ? caretOffset() : null
		const before = emitted.current ?? ''
		emitted.current = value
		rebuild(value)
		if (caret != null) {
			const pos = mapCaret(before, value, caret)
			setCaretOffset(pos)
			lastCaret.current = pos
		}
	}, [value]) // eslint-disable-line react-hooks/exhaustive-deps

	// Слоты обновились (загрузили/убрали фото, сменили карточку) — перерисовать
	// вид пилюль (подписи/пустоту), текст не трогаем.
	const slotsKey = JSON.stringify([
		slots.kind,
		slots.photos.map((p) => [p.label || '', p.url || '']),
		slots.firstFrame?.url || '', slots.lastFrame?.url || '', slots.character?.url || '', slots.character?.name || '',
		slots.videoRef?.url || '', slots.audioRef?.url || '', slots.audioRef?.name || '',
		slots.caps || null, // применимость входов к модели: смена модели перерисовывает пилюли (скрыть/показать)
		slots.refStyle || null, // синтаксис ссылок модели: смена модели меняет подписи (@Image1 ↔ @image_1 ↔ Image 1)
		slots.valueLabel || '',
		// Ассеты — только упомянутые в тексте и без подписи ссылки: список меню «@» (десятки ассетов) и свежая
		// подпись presigned-URL вид чипов не меняют, а каждая пересборка поля — лишняя перезагрузка миниатюр.
		slots.assets
			? assetIdsInPrompt(value).map((id) => {
					const a = slots.assets?.[id]
					return a ? [id, a.name, (a.url || '').split('?', 1)[0], a.kind] : [id]
				})
			: null,
		mode,
	])
	useEffect(() => {
		// Юзер может печатать в этот момент (подпись чипа ассета приехала с сервера, догрузилось фото): поле
		// пересобирается целиком — каретку возвращаем на место, иначе ввод уходил в начало промпта.
		const caret = ref.current && document.activeElement === ref.current ? caretOffset() : null
		rebuild(emitted.current ?? value)
		if (caret != null) setCaretOffset(caret)
	}, [slotsKey]) // eslint-disable-line react-hooks/exhaustive-deps

	const scrollPosIntoView = (pos: number) => {
		const root = ref.current
		if (!root) return
		// Ищем узел по текстовой позиции (пилюля = длина её фразы).
		let rest = pos
		let target: Node | null = null
		const walk = (node: Node): boolean => {
			for (const ch of Array.from(node.childNodes)) {
				if (ch.nodeType === Node.TEXT_NODE) {
					const len = (ch as Text).data.length
					if (rest <= len) { target = ch; return true }
					rest -= len
				} else if (ch.nodeType === Node.ELEMENT_NODE) {
					const el = ch as HTMLElement
					if (el.dataset && el.dataset.phrase != null) {
						const len = el.dataset.phrase.length
						if (rest <= len) { target = el; return true }
						rest -= len
					} else if (el.tagName === 'BR') {
						if (rest <= 1) { target = el; return true }
						rest -= 1
					} else if (walk(el)) return true
				}
			}
			return false
		}
		walk(root)
		const el = target && (target as Node).nodeType === Node.TEXT_NODE ? (target as Text).parentElement : (target as HTMLElement | null)
		if (!el) return
		const er = el.getBoundingClientRect()
		const rr = root.getBoundingClientRect()
		if (er.bottom > rr.bottom - 8) root.scrollTop += er.bottom - rr.bottom + 24
		else if (er.top < rr.top + 8) root.scrollTop -= rr.top - er.top + 24
	}

	useImperativeHandle(outerRef, () => ({
		focus: () => ref.current?.focus(),
		insertText: (text: string) => {
			const root = ref.current
			if (!root) return
			// Позиция вставки: каретка в поле → она (выделение заменяется); фокус
			// ушёл (кнопка-чип) → последняя запомненная позиция; иначе — конец.
			// Раньше root.focus() до чтения выделения ставил каретку в НАЧАЛО поля,
			// а после вставки каретка уезжала в конец — пилюля ложилась не туда.
			const sel = window.getSelection()
			let pos: number
			if (sel && sel.rangeCount > 0 && root.contains(sel.getRangeAt(0).startContainer)) {
				sel.getRangeAt(0).deleteContents()
				pos = caretOffset() ?? serialize().length
			} else {
				const len = serialize().length
				pos = lastCaret.current != null ? Math.min(lastCaret.current, len) : len
			}
			const cur = serialize()
			const v = cur.slice(0, pos) + text + cur.slice(pos)
			emitted.current = v
			onChangeRef.current(v)
			// Пересборка: вставленная фраза сразу становится пилюлей; каретка —
			// сразу за ней, юзер продолжает печатать с того же места.
			rebuild(v)
			root.focus({ preventScroll: true })
			const after = pos + text.length
			setCaretOffset(after)
			lastCaret.current = after
			scrollPosIntoView(after)
		},
		scrollPosIntoView,
		insertMention: (token: string) => {
			const root = ref.current
			const at = mentionRef.current
			if (!root) return
			const cur = serialize()
			// «@запрос» у каретки заменяется токеном; меню закрыто — вставка по месту каретки.
			const start = at ? at.start : lastCaret.current ?? cur.length
			const end = at ? at.start + at.query.length + 1 : start
			const tail = cur.slice(end)
			const v = cur.slice(0, start) + token + (tail.startsWith(' ') ? '' : ' ') + tail
			mentionRef.current = null
			onMentionRef.current?.(null)
			emitted.current = v
			onChangeRef.current(v)
			rebuild(v)
			root.focus({ preventScroll: true })
			const after = start + token.length + 1
			setCaretOffset(after)
			lastCaret.current = after
			scrollPosIntoView(after)
		},
		el: ref.current,
	}))

	const popData = pop
		? (() => {
				const view = pillView(pop.token, slots, mode)
				const url = mode === 'template' ? null : tokenUrl(pop.token, slots)
				if (pop.token.kind === 'asset') {
					const a = pop.token.assetId ? slots.assets?.[pop.token.assetId] : undefined
					return { view: { ...view, sub: a ? ASSET_KIND_LABEL[a.kind] || undefined : undefined }, url, phrase: null, modelToken: null, asset: true }
				}
				const phrase =
					pop.token.kind === 'photo'
						? phraseForRank(pop.token.rank ?? 1, slots.kind) || `Image ${pop.token.rank ?? 1}`
						: pop.token.kind === 'first_frame'
						? FIRST_FRAME_PHRASES[slots.kind]
						: pop.token.kind === 'last_frame'
						? LAST_FRAME_PHRASE
						: pop.token.kind === 'video_ref'
						? VIDEO_REF_PHRASE
						: pop.token.kind === 'audio_ref'
						? AUDIO_REF_PHRASE
						: CHARACTER_PHRASE
				return {
					view,
					url,
					// Режим «шаблон»: «на сайте это «Image 1»» — фраза, в которую превратится токен.
					phrase: mode === 'template' ? phraseForToken(pop.token, slots.kind) : phrase,
					modelToken: refTokenLabel(pop.token, slots.refStyle),
					asset: false,
				}
			})()
		: null

	return (
		<div ref={wrapRef} className="gm__ppill-wrap">
			<div
				ref={ref}
				className={className}
				contentEditable={!disabled}
				suppressContentEditableWarning
				role="textbox"
				aria-multiline="true"
				aria-label="Промпт"
				data-placeholder={placeholder || ''}
				spellCheck={false}
				onInput={emit}
				onKeyDown={(e) => {
					// Меню «@» открыто: стрелки/Enter/Tab/Esc ведёт хост (выбор ассета).
					if (mentionRef.current && onMentionKey && ['ArrowUp', 'ArrowDown', 'Enter', 'Tab', 'Escape'].includes(e.key)) {
						if (onMentionKey(e.key)) {
							e.preventDefault()
							if (e.key === 'Escape') {
								mentionRef.current = null
								onMentionRef.current?.(null)
							}
							return
						}
					}
					// Enter → перенос строки (не div-обёртки браузера).
					if (e.key === 'Enter') {
						e.preventDefault()
						document.execCommand('insertLineBreak')
						return
					}
					// Лимит длины (Suno): блокируем ввод символов сверх лимита,
					// служебные клавиши (Backspace/стрелки/сочетания) не трогаем.
					if (
						maxLength != null &&
						e.key.length === 1 &&
						!e.metaKey && !e.ctrlKey && !e.altKey &&
						serialize().length >= maxLength &&
						(window.getSelection()?.isCollapsed ?? true)
					) e.preventDefault()
				}}
				onPaste={(e) => {
					e.preventDefault()
					let text = e.clipboardData.getData('text/plain')
					if (maxLength != null) {
						const room = maxLength - serialize().length + (window.getSelection()?.toString().length || 0)
						text = text.slice(0, Math.max(0, room))
					}
					if (!text) return
					document.execCommand('insertText', false, text)
					// Пересборка, чтобы вставленные фразы стали пилюлями; ссылки «руками» во вставке — сразу переменными.
					const v = serialize()
					if (!convertRefs(v, null, true)) {
						emitted.current = v
						onChangeRef.current(v)
						rebuild(v)
					}
					const sel = window.getSelection()
					const r = document.createRange()
					if (ref.current) {
						r.selectNodeContents(ref.current)
						r.collapse(false)
						sel?.removeAllRanges()
						sel?.addRange(r)
					}
				}}
				onBlur={() => {
					hidePopSoon()
					// Меню «@» живёт в портале: клик по пункту приходит раньше blur (mousedown preventDefault у хоста),
					// так что закрытие по уходу фокуса не мешает выбору.
					if (mentionRef.current) {
						mentionRef.current = null
						onMentionRef.current?.(null)
					}
				}}
				onDragOver={(e) => { if (dragChip.current) { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'move' } }}
				onDrop={(e) => {
					const chip = dragChip.current
					if (!chip || !ref.current) return
					e.preventDefault()
					const doc = document as Document & { caretRangeFromPoint?: (x: number, y: number) => Range | null }
					let range = doc.caretRangeFromPoint ? doc.caretRangeFromPoint(e.clientX, e.clientY) : null
					if (!range) return
					// Позиция внутри другой пилюли (contenteditable=false) — ставим ПЕРЕД ней.
					let host: Node | null = range.startContainer
					while (host && host !== ref.current) {
						if ((host as HTMLElement).dataset?.phrase != null) {
							range = document.createRange()
							range.setStartBefore(host)
							break
						}
						host = host.parentNode
					}
					range.collapse(true)
					range.insertNode(chip) // существующий узел перемещается
					dragChip.current = null
					// Эмит вне обработчика drop (синхронный dispatch React глотает).
					setTimeout(() => { emit() }, 0)
				}}
			/>
			{pop && popData && createPortal(
				<div
					className="gm__ppill-pop gm__ppill-pop--fixed"
					style={{ left: pop.left, top: pop.below ? pop.top : undefined, bottom: pop.below ? undefined : window.innerHeight - pop.top }}
					onMouseEnter={() => { if (popTimer.current) clearTimeout(popTimer.current) }}
					onMouseLeave={hidePopSoon}
				>
					{mode === 'template' ? null : popData.url ? (
						pop.token.kind === 'video_ref'
							? <video src={popData.url} className="gm__ppill-pop__img" muted playsInline preload="metadata" />
							: pop.token.kind === 'audio_ref'
							? <div className="gm__ppill-pop__img gm__ppill-pop__img--audio"><audio src={popData.url} controls preload="metadata" style={{ width: '100%' }} /></div>
							: <img src={popData.url} alt="" className="gm__ppill-pop__img" />
					) : popData.asset ? null : (
						<div className="gm__ppill-pop__img gm__ppill-pop__img--empty">{pop.token.kind === 'video_ref' ? 'видео не загружено' : pop.token.kind === 'audio_ref' ? 'аудио не загружено' : 'фото не загружено'}</div>
					)}
					<div className="gm__ppill-pop__title">{popData.view.main}{popData.view.sub ? ` · ${popData.view.sub}` : ''}</div>
					{popData.phrase && (
						<div className="gm__ppill-pop__sub">
							{mode === 'template'
								? <>на сайте это <code>{popData.phrase}</code>{popData.modelToken ? <>, модель получит <code>{popData.modelToken}</code></> : null}</>
								: popData.modelToken
									? <>модель получит это как <code>{popData.modelToken}</code></>
									: <>в промпте это <code>{popData.phrase}</code></>}
						</div>
					)}
					{popData.asset && (
						<div className="gm__ppill-pop__sub">нейросеть получит это имя и фото из сцены — какое фото чьё, мы подпишем сами</div>
					)}
					{mode !== 'template' && !popData.url && !popData.asset && <div className="gm__ppill-pop__sub gm__ppill-pop__sub--warn">загрузите фото в этот слот выше — иначе модели не на что сослаться</div>}
				</div>,
				document.body,
			)}
		</div>
	)
})

export default PromptPillEditor
